<template>
  <div class="output">
    <n-header/>
    <article class="visualization">
      <section class="crop">
        <canvas class="crop-canvas" ref="canvas"></canvas>
        <div class="crop-data">
          <header>
            <h2>{{$t(`crops.${cropCode}`)}}</h2>
            <h3>{{currentDate}}</h3>
          </header>
          <div class="main">
            <div class="column">
              <div class="data-element">
                <icon-base class="icon">
                  <icon-irrigation />
                </icon-base>
                <div class="label">
                  {{$t('irrigation-precipitation')}}
                </div>
                <div class="value">
                  <span class="number" v-html="currentDayPrecipitationAndIrrigation"></span>
                  <span class="unit">mm</span>
                </div>
              </div>
              <div class="data-element">
                <icon-base class="icon">
                  <icon-fertilization />
                </icon-base>
                <div class="label">
                  {{$t('applied-fertilizer')}}
                </div>
                <div class="value">
                  <span class="number" v-html="currentDayAppliedFertilizer"></span>
                  <span class="unit">kg N/ha</span>
                </div>
              </div>
              <div class="data-element">
                <icon-base class="icon">
                  <icon-uptake />
                </icon-base>
                <div class="label">
                  {{$t('n-uptake')}}
                </div>
                <div class="value">
                  <span class="number" v-html="currentDayNitrogenUptake"></span>
                  <span class="unit">kg N/ha</span>
                </div>
              </div>
            </div>
            <div class="column">
              <div class="data-element">
                <div class="label">BBCH</div>
                <div class="value">{{Math.round(currentDayBbch)}}</div>
              </div>
            </div>
          </div>
          <footer>
            <div class="data-element">
              <icon-base class="icon">
                <icon-leaching />
              </icon-base>
              <div class="label">
                {{$t('n-leaching')}}
              </div>
              <div class="value">
                <span class="number" v-html="currentDayLeaching"></span>
                <span class="unit">kg N/ha</span>
              </div>
            </div>
          </footer>
        </div>
      </section>
      <section class="charts">
        <header>
          <nav class="tab-menu">
            <a
              key="tab-in-season-processes"
              class="tab-button"
              :class="{ 'selected': isTabSelected('tab-in-season-processes')}"
              @click.prevent="handleTabClick('tab-in-season-processes')">
              {{$t('in-season-processes')}}
            </a>
            <a
              key="tab-n-balance"
              class="tab-button"
              :class="{ 'selected': isTabSelected('tab-n-balance')}"
              @click.prevent="handleTabClick('tab-n-balance')">
              {{$t('n-balance')}}
            </a>
            <a
              key="tab-report"
              class="tab-button"
              :class="{ 'selected': isTabSelected('tab-report')}"
              @click.prevent="handleTabClick('tab-report')">
              {{$t('report')}}
            </a>
          </nav>
        </header>
        <div class="charts-content" v-if="isTabSelected('tab-in-season-processes')">
          <h3>{{$t('n-leaching-rate')}}</h3>
          <n-leaching-rate-chart
            :buffer="buffer"
            :bufferedMin="bufferedMin"
            :bufferedMax="bufferedMax"
            :progress="progress"
            :days="days"
          />
          <h3>{{$t('cumulative-n-leaching')}}</h3>
          <n-leaching-cumulative-chart
            :buffer="buffer"
            :bufferedMin="bufferedMin"
            :bufferedMax="bufferedMax"
            :progress="progress"
            :days="days"
          />
        </div>
        <div class="charts-content" v-if="isTabSelected('tab-n-balance')">
          <h3>{{$t('n-balance')}}</h3>
          <n-pie-chart
            class="graph"
            :data="[nitrogenUptake, nitrogenRemain, nitrogenLeach]"
            :colors="['#248F79', '#EBAF47', '#C45F4F']"
            :legends="['n-uptake','n-remain','n-leach']"
          />
          <h3>{{$t('n-balance-end-year')}}</h3>
          <n-pie-chart
            class="graph"
            :data="[nitrogenUptakeEndYear, nitrogenRemainAtHarvest, nitrogenRemainAtEndOfYear, nitrogenLeachInSeason, nitrogenLeachPostSeason]"
            :colors="['#248F79', '#EBAF47', '#CC9433', '#C45F4F', '#86392D']"
            :legends="['n-uptake','n-remain-harvest','n-remain-end-year','n-leach-in-season', 'n-leach-post-season']"
          />
        </div>
        <div class="charts-content" v-if="isTabSelected('tab-report')">
          <h3>{{$t('report')}}</h3>
          <n-report-item
            :label="$t('location')"
            :value="regionName" />
          <n-report-item
            :label="$t('crop')"
            :value="$t(`crops.${cropCode}`)" />
          <n-report-item
            :label="$t('soil-type')"
            :value="$t(`soils.${soilCode}`)" />
          <n-report-item
            :label="$t('cumulative-rainfall')"
            :value="cummulativePrecipitation"
            unit="mm" />
          <n-report-item
            :label="$t('cumulative-irrigation')"
            :value="cummulativeIrrigation"
            unit="mm" />
          <n-report-item
            :label="$t('n-input-mineral-fertilizer')"
            :value="mineralFertilizer"
            unit="kg N / ha" />
          <n-report-item
            :label="$t('n-input-organic-fertilizer')"
            :value="organicFertilizer"
            unit="kg N / ha" />
          <n-report-item
            :label="$t('Expeted yield')"
            :value="cropYield"
            unit="t / ha" />
          <n-report-item
            :label="$t('n-removal-by-crop')"
            :value="nitrogenRemovalByCrop"
            unit="kg N / ha" />
          <n-report-item
            :label="$t('n-in-soil')"
            :value="nitrogenInSoil"
            unit="kg N / ha" />
          <n-report-item
            :label="$t('n-in-topsoil')"
            :value="nitrogenInTopSoil"
            unit="kg N / ha" />
          <!-- TODO: adaptar el dato de la profundidad a la profundidad de la raíz -->
          <n-report-item
            :label="$t('n-in-subsoil')"
            :value="nitrogenInSubSoil"
            unit="kg N / ha" />
          <n-report-item
            :label="$t('n-leached-in-season')"
            :value="nitrogenLeachedInSeason"
            unit="kg N / ha" />
          <!-- TODO: el tiempo señalado debe ser igual a lo que dura la simulación -->
          <n-report-item
            :label="$t('n-leached-over')"
            :value="nitrogenLeachedOverTime"
            unit="kg N / ha" />
        </div>
      </section>
      <n-tooltip v-if="tooltipShown"></n-tooltip>
      <n-control-bar
        :start="start"
        :end="end"
        :current="current"
        :buffered="buffered"
        :is-buffering="isBuffering"
        :is-playing="isPlaying"
        custom-class="control-bar"
        @over="handleControlBarOver"
        @out="handleControlBarOut"
        @seek="handleControlBarSeek"
        @play="handleControlBarPlay"
        @pause="handleControlBarPause" />
    </article>
  </div>
</template>

<style lang="scss" scoped>

.visualization {
  height: calc(100vh - 4rem);
  display: grid;
  grid-template-columns: 3fr 2fr;
  grid-template-rows: 1fr 4rem;
  grid-template-areas:
    "crop charts"
    "control-bar control-bar"
}

.crop {
  grid-area: crop;
  position: relative;
  display: flex;
  padding: 0 $spacing--m;
}

.crop-canvas {
  position: absolute;
  width: calc(100% - 4rem);
  height: 100%;
  z-index: -1;
}

.crop-data {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  header {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    padding-top: $spacing--s;
  }
  .main {
    flex: 1;
    display: flex;
    justify-content: space-between;
  }
  .column:last-child {
    text-align: right;
  }
}

.graph {
  margin-bottom: $spacing--l;
}

.data-element {
  margin-bottom: $spacing--s;
  display: grid;
  grid-template-areas:
    "icon label"
    "icon value";
  grid-template-columns: $spacing--m auto;
  .icon {
    color: var(--color--secondary);
    grid-area: icon;
    margin-top: 0.25rem;
  }
  .label {
    @extend %text-style--label;
    font-size: var(--text--xs);
    color: var(--color--secondary);
    grid-area: label;
  }
  .value {
    grid-area: value;
  }
}

.charts {
  grid-area: charts;
  border-left: 1px solid var(--color--outlines);
  overflow: auto;
  header {
    height: var(--height--header);
    box-shadow: 0 1px 0 0 var(--color--outlines);
    display: flex;
    justify-content: center;
    align-items: flex-end;
  }
  .charts-content {
    padding: $spacing--m;
    height: calc(100% - var(--height--header));
    overflow-y: auto;
  }
}

.control-bar {
  grid-area: control-bar;
  border-top: 1px solid var(--color--outlines);
}

</style>

<script>
import moment from 'moment'
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex'

import Header from '@/components/Header'
import ControlBar from '@/components/ControlBar'
import ReportItem from '@/components/ReportItem'
import IconBase from '@/components/icons/IconBase'
import IconIrrigation from '@/components/icons/IconIrrigation'
import IconFertilization from '@/components/icons/IconFertilization'
import IconUptake from '@/components/icons/IconUptake'
import IconLeaching from '@/components/icons/IconLeaching'
import Tooltip from '@/components/Tooltip'
import PieChart from '@/components/charts/PieChart'
import LeachingRateChart from '@/components/charts/LeachingRateChart'
import LeachingCumulativeChart from '@/components/charts/LeachingCumulativeChart'
import { Distribution } from '@/store/modules/constants'

import number from '@/helpers/number'

import simulation from '@/simulation'

let currentSimulation

function lastOr (array, selector, value) {
  const len = array.length

  return len === 0 ? value : selector(array[len - 1])
}

export default {
  name: 'output-view',
  components: {
    'n-header': Header,
    'n-control-bar': ControlBar,
    'n-report-item': ReportItem,
    'icon-base': IconBase,
    'icon-irrigation': IconIrrigation,
    'icon-fertilization': IconFertilization,
    'icon-uptake': IconUptake,
    'icon-leaching': IconLeaching,
    'n-tooltip': Tooltip,
    'n-pie-chart': PieChart,
    'n-leaching-rate-chart': LeachingRateChart,
    'n-leaching-cumulative-chart': LeachingCumulativeChart
  },
  data () {
    return {
      tabSelected: 'tab-in-season-processes'
    }
  },
  computed: {
    ...mapGetters({
      days: 'simulation/days',
      start: 'simulation/start',
      end: 'simulation/end',
      current: 'simulation/current',
      data: 'simulation/data',
      buffered: 'simulation/buffered',
      progress: 'simulation/progress'
    }),
    ...mapState({
      buffer: (state) => state.simulation.buffer,
      bufferedMin: (state) => state.simulation.bufferedMin,
      bufferedMax: (state) => state.simulation.bufferedMax,
      currentDate: (state) => moment(state.simulation.currentDate).format('DD-MM-YYYY'),
      region: (state) => state.region.type,
      regionName: (state) => state.region.type && state.region.type.name,
      soil: (state) => state.soil.type,
      soilCode: (state) => state.soil.type && state.soil.type.code,
      soilName: (state) => state.soil.type && state.soil.type.name,
      crop: (state) => state.crop.type,
      cropCode: (state) => state.crop.type && state.crop.type.code,
      cropName: (state) => state.crop.type && state.crop.type.name,
      cropYield: (state) => state.crop.MainYield,
      cropData: (state) => state.simulation.cropData,
      cropImages: (state) => state.simulation.cropImages,
      hasEnded: (state) => state.simulation.hasEnded,
      hasStarted: (state) => state.simulation.hasStarted,
      isPlaying: (state) => state.simulation.isPlaying,
      isBuffering: (state) => state.simulation.isBuffering,
      layers: (state) => state.crop.layers,
      mineralFertilizer: (state) => state.fertilizer.applications.reduce((acc, current) => acc + (!state.fertilizer.fertigation ? current.rate : (current.distribution === Distribution.ONE_TIME ? current.rate : (moment(current.toDate).diff(current.fromDate).days() / current.every) * current.rate)), 0),
      organicFertilizer: (state) => state.fertilizer.organicApplications.reduce((acc, current) => acc + current.rate, 0),
      cummulativePrecipitation: (state) => state.simulation.buffer.reduce((acc, current) => acc + current.Pcp, 0).toFixed(0),
      cummulativeIrrigation: (state) => state.simulation.buffer.reduce((acc, current) => acc + current.Irri, 0).toFixed(0),
      nitrogenRemovalByCrop: (state) => state.simulation.buffer.reduce((a, c) => a + c.anup_take, 0.0).toFixed(2),
      nitrogenInSoil: (state) => state.simulation.buffer.reduce((acc, current) => current.main_content, 0).toFixed(2),
      nitrogenInTopSoil: (state) => state.simulation.buffer.reduce((acc, current) => current.upper_content, 0).toFixed(2),
      nitrogenInSubSoil: (state) => state.simulation.buffer.reduce((acc, current) => current.lower_content, 0).toFixed(2),
      nitrogenLeachedInSeason: (state) => state.simulation.buffer.reduce((a, c) => a + c.NLeached, 0.0).toFixed(2),
      nitrogenLeachedOverTime: (state) => state.simulation.buffer.reduce((a, c) => a + c.NLeached, 0.0).toFixed(2),
      nitrogenUptake: (state) => state.simulation.buffer.reduce((a, c) => a + c.anup_take, 0.0),
      nitrogenRemain: (state) => lastOr(state.simulation.buffer, v => v.main_content, 0.0),
      nitrogenLeach: (state) => state.simulation.buffer.reduce((a, c) => a + c.NLeached, 0.0),
      nitrogenUptakeEndYear: (state) => state.simulation.buffer.reduce((a, c) => a + c.anup_take, 0.0),
      nitrogenRemainAtHarvest: (state) => lastOr(state.simulation.buffer, v => v.main_content, 0.0),
      nitrogenRemainAtEndOfYear: (state) => lastOr(state.simulation.buffer, v => 0.0),
      nitrogenLeachInSeason: (state) => state.simulation.buffer.reduce((a, c) => a + c.NLeached, 0.0),
      nitrogenLeachPostSeason: (state) => state.simulation.buffer.reduce((a, c) => 0.0),
      currentDayPrecipitationAndIrrigation: (_, getters) => getters['simulation/precipitationAndIrrigation'].toFixed(1),
      currentDayAppliedFertilizer: (_, getters) => number.formatNContent(getters['simulation/appliedFertilizer']),
      currentDayNitrogenUptake: (_, getters) => number.formatNContent(getters['simulation/nitrogenUptake']),
      currentDayLeaching: (_, getters) => number.formatNContent(getters['simulation/nitrogenLeaching']),
      currentDayBbch: (_, getters) => getters['simulation/data'] && getters['simulation/data'].BBCH,
      tooltipShown: (state) => state.tooltip.shown
    })
  },
  watch: {
    isPlaying (value) {
      if (currentSimulation && value === false) {
        currentSimulation.pause()
      } else if (currentSimulation && value === true) {
        currentSimulation.resume()
      }
    },
    data (value, oldValue) {
      if (currentSimulation) {
        currentSimulation.seek(value, value && oldValue && value.Date !== oldValue.Date)
      }
    }
  },
  methods: {
    ...mapMutations({
      tooltipShow: 'tooltip/show',
      tooltipHide: 'tooltip/hide'
    }),
    ...mapActions({
      seek: 'simulation/seek',
      play: 'simulation/play',
      pause: 'simulation/pause',
      loadCrop: 'crop/loadData'
    }),
    /* End of chart functions */
    isTabSelected (item) {
      return this.tabSelected === item
    },
    handleTabClick (item) {
      this.tabSelected = item
    },
    handleControlBarOut () {
      this.tooltipHide()
    },
    handleControlBarOver ({ time, x, y }) {
      const color = getComputedStyle(document.documentElement).getPropertyValue('--color--primary')
      this.tooltipShow({
        text: moment(time).format('DD-MM-YYYY'),
        x,
        y,
        color: color
      })
    },
    handleControlBarSeek (time) {
      this.seek(time)
    },
    handleControlBarPlay () {
      this.play()
    },
    handleControlBarPause () {
      this.pause()
    }
  },
  created () {
    if (!this.crop && !this.hasStarted) {
      this.$router.replace('/input')
    }
  },
  async mounted () {
    currentSimulation = await simulation.init(this.$refs.canvas, await this.loadCrop(this.crop))
    if (currentSimulation) {
      currentSimulation.start(this.data)
    }
  },
  beforeDestroy () {
    if (currentSimulation) {
      currentSimulation.stop()
    }
    currentSimulation = undefined
  }
}
</script>
